import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { MieterZeile, mieterDetail, mieterSuchen } from "../../worker/data/mieter";
import type { MieterEingabe } from "@shared/schemas/stammdaten";

export type { MieterZeile, MieterEingabe };
export type MieterDetail = Awaited<ReturnType<typeof mieterDetail>>;
export type MieterTreffer = Awaited<ReturnType<typeof mieterSuchen>>[number];

const K = ["laden", "mieter"] as const;

function nachAenderung() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: K });
    void qc.invalidateQueries({ queryKey: ["laden", "uebersicht"] });
  };
}

export const useMieterListe = () => useQuery({ queryKey: K, queryFn: () => rufe<{ mieter: MieterZeile[] }>(api.api.laden.mieter.$get()) });
export const useMieterDetail = (id: string) => useQuery({ queryKey: [...K, id], queryFn: () => rufe<MieterDetail>(api.api.laden.mieter[":id"].$get({ param: { id } })), enabled: !!id });
export const useMieterSuche = (q: string) => useQuery({ queryKey: [...K, "suche", q], queryFn: () => rufe<{ treffer: MieterTreffer[] }>(api.api.laden.mieter.suche.$get({ query: { q } })), enabled: q.trim().length > 0, staleTime: 5_000 });
export const useNaechsteMieternummer = (aktiv: boolean) => useQuery({ queryKey: [...K, "naechste-nummer"], queryFn: () => rufe<{ mieternummer: string }>(api.api.laden.mieter["naechste-nummer"].$get()), enabled: aktiv, staleTime: 0 });

export function useMieterAnlegen() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (d: MieterEingabe) => rufe<{ id: string; mieternummer: string }>(api.api.laden.mieter.$post({ json: d })), onSuccess: nach });
}
export function useMieterAendern() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { id: string } & MieterEingabe) => rufe(api.api.laden.mieter[":id"].$put({ param: { id: v.id }, json: v })), onSuccess: nach });
}
export function useMieterStatus() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { id: string; status: "aktiv" | "gesperrt" | "archiviert"; grund?: string }) => rufe(api.api.laden.mieter[":id"].status.$post({ param: { id: v.id }, json: { status: v.status, grund: v.grund ?? "" } })), onSuccess: nach });
}
export function useIbanAnzeigen() {
  return useMutation({ mutationFn: (id: string) => rufe<{ iban: string | null }>(api.api.laden.mieter[":id"].iban.$post({ param: { id } })) });
}

export interface Hinweis { id: string; art: "aufraeumen" | "rueckgabe_kiste" | "mietende" | "info"; text: string; erstelltAt: string; gelesenAt: string | null }
export const useHinweise = (mieterId: string) => useQuery({ queryKey: [...K, mieterId, "hinweise"], queryFn: () => rufe<{ hinweise: Hinweis[] }>(api.api.laden.mieter[":id"].hinweise.$get({ param: { id: mieterId } })), enabled: !!mieterId });
export function useHinweisSenden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { mieterId: string; art: "aufraeumen" | "mietende" | "info"; text: string }) => rufe<{ id: string }>(api.api.laden.mieter[":id"].hinweise.$post({ param: { id: v.mieterId }, json: { art: v.art, text: v.text } })),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: [...K, v.mieterId, "hinweise"] }),
  });
}
